import { caretAfterPill } from '@/components/admin/variables/variable-pill';
import type {
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Quand la fiche d'une pastille s'ouvre, se fixe et se ferme (refonte
 * modulaire §8.6 ; grill Variables du 2026-09-16, D6 ; grill du 2026-09-23 ;
 * maquette `variables.html`, planche 3).
 *
 * « Au survol (300 ms) ou au focus clavier, une fiche … ; un clic la fixe,
 * Échap la ferme. » The mockup is interactive precisely because this is a
 * BEHAVIOUR, and this hook is that behaviour written once for the five zones:
 *
 *   - hovering a pill arms a 300 ms timer; leaving it before the timer fires
 *     disarms it, leaving it after closes the fiche - unless it is pinned;
 *   - focusing a pill opens the fiche at once, blurring it closes it - unless
 *     pinned - because the keyboard has no « hover » to wait on;
 *   - a click PINS the fiche on this pill; a second click on the same pill
 *     releases it; a click on another pill moves the pin;
 *   - in an EDITABLE surface that same click ALSO places the caret just after
 *     the pill (grill du 2026-09-23);
 *   - Échap closes whatever is open, pinned or not; so does a `mousedown`
 *     anywhere outside the fiche and the pill it stands on.
 *
 * ## Delegated to the ZONE, never attached to the pill
 *
 * The handlers are spread on the zone's container and find the pill by
 * `closest('[data-variable]')`. Three of the five zones are painted
 * `contentEditable` surfaces whose pills are built imperatively and replaced on
 * every repaint: a listener attached to the pill would be attached to a node
 * about to be thrown away. Delegation also gives the two React-drawn surfaces
 * the same behaviour for free.
 *
 * ## The `mousedown` is intercepted, and THAT is where the caret is decided (#138)
 *
 * D6 gives the pill a `tabindex`, and inside a `contentEditable` a focusable
 * island is a caret trap: the browser's default `mousedown` moves the focus
 * onto the pill and drops the caret inside its non-editable label, where no
 * keystroke does anything - the defect reported on 2026-09-23. One
 * `preventDefault` on `mousedown` refuses that whole default, focus theft and
 * caret placement alike, and `caretAfterPill` then puts the caret where a click
 * on a pill means to put it. `mousedown` and not `click`, because the caret is
 * placed by the FIRST of the two: by the time `click` fires the damage is done,
 * which is why `onClick`'s `preventDefault` never mattered.
 *
 * The guard is `isContentEditable` on the zone's own container, so the two
 * READ-ONLY zones - the panel « Ce que l'interviewer recevra », the persona
 * directive - and a field mounted `readOnly` keep the plain browser behaviour:
 * they are meant to be read and selected, and a caret in them would promise an
 * edit that is refused. The primary button only: a right-click opens a context
 * menu and moves nothing.
 *
 * ## What the hook does NOT know
 *
 * Which variable the pill names and what the fiche says: it hands back the
 * anchor element and its `data-variable`, and the zone asks its own describer.
 * A hook that resolved variables would have to know about an experience, and
 * the protocol's direction field mounts it without one.
 */

export const FICHE_HOVER_DELAY_MS = 300;

/** The pill the fiche stands on, and the token it names. */
export type FicheAnchor = {
  readonly element: HTMLElement;
  readonly token: string;
};

export interface VariableFicheHandlers {
  onMouseOver: (event: ReactMouseEvent<HTMLElement>) => void;
  onMouseOut: (event: ReactMouseEvent<HTMLElement>) => void;
  onMouseDown: (event: ReactMouseEvent<HTMLElement>) => void;
  onFocus: (event: ReactFocusEvent<HTMLElement>) => void;
  onBlur: (event: ReactFocusEvent<HTMLElement>) => void;
  onClick: (event: ReactMouseEvent<HTMLElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

export interface VariableFicheState {
  readonly anchor: FicheAnchor | null;
  readonly pinned: boolean;
  /** To spread on the zone's container. */
  readonly handlers: VariableFicheHandlers;
  /** For the fiche itself: a `mousedown` inside it must not close it. */
  readonly ficheRef: (node: HTMLElement | null) => void;
  readonly close: () => void;
}

/** The pill an event landed on, or null when it landed beside one. */
function pillOf(target: EventTarget | null, within: HTMLElement): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const pill = target.closest<HTMLElement>('[data-variable]');
  if (pill === null || !within.contains(pill)) return null;
  return pill;
}

function anchorOf(pill: HTMLElement): FicheAnchor {
  return { element: pill, token: pill.dataset.variable ?? '' };
}

export function useVariableFiche(): VariableFicheState {
  const [anchor, setAnchor] = useState<FicheAnchor | null>(null);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fiche = useRef<HTMLElement | null>(null);

  const disarm = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const close = useCallback(() => {
    disarm();
    setAnchor(null);
    setPinned(false);
  }, [disarm]);

  // The timer must not fire on a zone that has gone.
  useEffect(() => disarm, [disarm]);

  /*
    Échap and the outside `mousedown` are DOCUMENT listeners, mounted only while
    a fiche is open: a zone that listened permanently would be one listener per
    text zone of the page for a fiche nobody has opened. `mousedown` and not
    `click`, for the reason `action-menu.tsx` states - the very click that pins
    would otherwise bubble to the document and close what it just opened.
  */
  useEffect(() => {
    if (anchor === null) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') close();
    }
    function onDown(event: MouseEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (fiche.current?.contains(target) === true) return;
      if (anchor?.element.contains(target) === true) return;
      close();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, close]);

  /*
    A repaint of the zone throws its pills away. The fiche then stands on an
    element that is no longer in the document, which `getBoundingClientRect`
    answers with zeros: it would jump to the corner of the window. So a detached
    anchor closes the fiche, checked after every render of the zone.
  */
  useEffect(() => {
    if (anchor !== null && !anchor.element.isConnected) close();
  });

  const handlers: VariableFicheHandlers = {
    onMouseOver: (event) => {
      const pill = pillOf(event.target, event.currentTarget);
      if (pill === null || pinned) return;
      if (anchor?.element === pill) return;
      disarm();
      timer.current = setTimeout(() => {
        timer.current = null;
        setAnchor(anchorOf(pill));
      }, FICHE_HOVER_DELAY_MS);
    },
    onMouseOut: (event) => {
      const pill = pillOf(event.target, event.currentTarget);
      if (pill === null) return;
      // Moving from the pill onto one of its own text nodes is not leaving it.
      if (event.relatedTarget instanceof Node && pill.contains(event.relatedTarget)) return;
      disarm();
      if (!pinned) setAnchor(null);
    },
    // The caret, and nothing else: pinning stays on the `click` - see the head.
    onMouseDown: (event) => {
      if (event.button !== 0) return;
      const host = event.currentTarget;
      if (!host.isContentEditable) return;
      const pill = pillOf(event.target, host);
      if (pill === null) return;
      event.preventDefault();
      caretAfterPill(host, pill);
    },
    onFocus: (event) => {
      const pill = pillOf(event.target, event.currentTarget);
      if (pill === null || pinned) return;
      disarm();
      setAnchor(anchorOf(pill));
    },
    onBlur: (event) => {
      const pill = pillOf(event.target, event.currentTarget);
      if (pill === null || pinned) return;
      disarm();
      setAnchor(null);
    },
    onClick: (event) => {
      const pill = pillOf(event.target, event.currentTarget);
      if (pill === null) return;
      event.preventDefault();
      disarm();
      if (pinned && anchor?.element === pill) {
        close();
        return;
      }
      setAnchor(anchorOf(pill));
      setPinned(true);
    },
    onKeyDown: (event) => {
      if (event.key === 'Escape' && anchor !== null) {
        event.preventDefault();
        close();
      }
    },
  };

  return {
    anchor,
    pinned,
    handlers,
    ficheRef: (node) => {
      fiche.current = node;
    },
    close,
  };
}
